import { PAGES, renamedHref } from '@alphadesk/web/links.ts';
import { useCallback, useEffect, useState } from 'react';

interface Route {
  path: string;
  query: URLSearchParams;
}

const read = (): Route => ({
  path: window.location.pathname,
  query: new URLSearchParams(window.location.search),
});

// What a row link carries: `recommendationLink` and `positionLink` write them on `/`.
const ROW_KEYS = ['recommendation', 'position'] as const;

/**
 * An old address is read as its new home, in the inbox's stored notes as much as on a load. `/` is
 * Home, except with a row's query, which is Trades' (the roll sweep knows a note by its href).
 */
function landing(href: string): string {
  const renamed = renamedHref(href) ?? href;
  if (!renamed.startsWith('/?')) return renamed;
  const query = new URLSearchParams(renamed.slice(1).split('#', 1)[0]);
  return ROW_KEYS.some((key) => query.has(key)) ? `${PAGES.trades}${renamed.slice(1)}` : renamed;
}

/** A hash names a section that may still be loading: look for it for a few seconds. */
function reveal(hash: string): void {
  const id = decodeURIComponent(hash.slice(1));
  if (id === '') return;
  const until = performance.now() + 3000;
  const look = () => {
    const there = document.getElementById(id);
    if (there) there.scrollIntoView({ block: 'start' });
    else if (performance.now() < until) requestAnimationFrame(look);
  };
  requestAnimationFrame(look);
}

/** So an old address never paints; replaced, as there is nothing to go back to. */
function land(): void {
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const there = landing(here);
  if (there !== here) history.replaceState(null, '', there);
}

/** Before the first render. Back and forward land too, but leave the scroll to the browser. */
export function openAt(): void {
  land();
  reveal(window.location.hash);
}

const listeners = new Set<() => void>();

export function navigate(href: string, { replace = false } = {}): void {
  const to = landing(href);
  if (replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  for (const listener of listeners) listener();
  reveal(window.location.hash);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const sync = () =>
      setRoute((was) => {
        const now = read();
        return now.path === was.path && now.query.toString() === was.query.toString() ? was : now;
      });
    // Back or forward can land on an old address from before a rename.
    const popped = () => {
      land();
      sync();
    };
    listeners.add(sync);
    window.addEventListener('popstate', popped);
    // A navigation between the render and this subscription was heard by nobody.
    sync();
    return () => {
      listeners.delete(sync);
      window.removeEventListener('popstate', popped);
    };
  }, []);
  return route;
}

/** Rewrites the query in place, keeping the path and the hash. No navigation and no route update: not a new page view. */
export function replaceQuery(query: URLSearchParams | string): void {
  const rest = query.toString();
  history.replaceState(
    null,
    '',
    `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`,
  );
}

export function forgetQuery(keys: readonly string[]): void {
  const query = new URLSearchParams(window.location.search);
  if (!keys.some((key) => query.has(key))) return;
  for (const key of keys) query.delete(key);
  replaceQuery(query);
}

/** State kept in the query, so a link or a reload opens on it. `serialise` touches no key it does not own. */
export function useQueryState<T extends object>(
  parse: (search: string) => T,
  serialise: (value: T, search: string) => string,
): [T, (patch: Partial<T>) => void] {
  const [value, setValue] = useState(() => parse(window.location.search));

  useEffect(() => {
    replaceQuery(serialise(value, window.location.search));
  }, [value, serialise]);

  const patch = useCallback((change: Partial<T>) => {
    setValue((current) => ({ ...current, ...change }));
  }, []);

  return [value, patch];
}

/** `/auth/…` is the server's: a redirect out to a sign-in or a broker, never a page of this app. */
const staysInApp = (href: string): boolean => href.startsWith('/') && !href.startsWith('/auth/');

export function useLinkClick(): (event: React.MouseEvent<HTMLAnchorElement>) => void {
  return useCallback((event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const href = event.currentTarget.getAttribute('href');
    if (href === null || !staysInApp(href)) return;
    event.preventDefault();
    navigate(href);
  }, []);
}
